import React from "react";
import { render, screen, within } from "@testing-library/react";
import "@testing-library/jest-dom";

import { FullPageChatLayout } from "./FullPageChatLayout";

jest.mock("@metalmind-ab/mmagent-chat-ui", () => ({
  ChatMessage: ({
    message,
  }: {
    message: { id: string; content: string; isError?: boolean };
  }) => (
    <article
      data-testid={`chat-message-${message.id}`}
      data-error={message.isError ? "true" : undefined}
    >
      {message.content}
    </article>
  ),
  JobProgressIndicator: () => null,
  useChatStore: (selector: (state: any) => any) =>
    selector({
      backgroundJobs: [],
      removeBackgroundJob: jest.fn(),
      messages: [],
    }),
}));

beforeAll(() => {
  Object.defineProperty(HTMLElement.prototype, "scrollIntoView", {
    configurable: true,
    value: jest.fn(),
  });
  Object.defineProperty(URL, "createObjectURL", {
    configurable: true,
    value: jest.fn(() => "blob:test-url"),
  });
  Object.defineProperty(URL, "revokeObjectURL", {
    configurable: true,
    value: jest.fn(),
  });
});

const renderLayout = (
  messages: any[],
  gptLiveVoiceControl?: React.ReactNode,
  gptLiveVoiceFeedback?: { message: string; tone: "danger" | "neutral" } | null,
) =>
  render(
    <FullPageChatLayout
      messages={messages}
      onSendMessage={jest.fn()}
      isLoading={false}
      conversations={[]}
      onLoadConversation={jest.fn()}
      onNewConversation={jest.fn()}
      onSearchConversations={jest.fn()}
      onLoadMoreConversations={jest.fn()}
      conversationsLoading={false}
      currentConversationId={42}
      welcomeTitle="Welcome"
      welcomeDescription=""
      gptLiveVoiceControl={gptLiveVoiceControl}
      gptLiveVoiceFeedback={gptLiveVoiceFeedback}
    />,
  );

describe("agent-originated assistant rows", () => {
  it("renders successive contents through the standard chat message path", () => {
    const message = {
      id: "agent-reply-1",
      role: "assistant" as const,
      content: "Checking the workspace",
      createdAt: new Date("2026-10-02T15:00:00Z"),
      metadata: { origin: "agent" },
    };
    const view = renderLayout([message]);

    expect(screen.getByTestId("chat-message-agent-reply-1")).toHaveTextContent(
      "Checking the workspace",
    );

    view.rerender(
      <FullPageChatLayout
        messages={[{ ...message, content: "The agent found the answer." }]}
        onSendMessage={jest.fn()}
        isLoading={false}
        conversations={[]}
        onLoadConversation={jest.fn()}
        onNewConversation={jest.fn()}
        onSearchConversations={jest.fn()}
        onLoadMoreConversations={jest.fn()}
        conversationsLoading={false}
        currentConversationId={42}
        welcomeTitle="Welcome"
        welcomeDescription=""
      />,
    );

    expect(screen.getByTestId("chat-message-agent-reply-1")).toHaveTextContent(
      "The agent found the answer.",
    );
  });

  it("shows a passive pre-turn-start error and ends the Working state", () => {
    const error = {
      id: "voice-prestart-error",
      role: "assistant" as const,
      content: "The request could not be completed.",
      createdAt: new Date("2026-10-23T15:00:00Z"),
      isStreaming: false,
      isWorking: false,
      isError: true,
    };

    renderLayout([error]);

    expect(
      screen.getByTestId("chat-message-voice-prestart-error"),
    ).toHaveTextContent("The request could not be completed.");
    expect(
      screen.getByTestId("chat-message-voice-prestart-error"),
    ).toHaveAttribute("data-error", "true");
    expect(
      screen.queryByTestId("chat-working-indicator"),
    ).not.toBeInTheDocument();
  });
});

describe("GPT-Live composer layout", () => {
  it("renders voice feedback in a full-width flow row above the composer", () => {
    renderLayout([], undefined, {
      message: "Voice could not connect.",
      tone: "danger",
    });

    const feedback = screen.getByTestId("gpt-live-feedback-row");
    const inputRow = screen.getByTestId("chat-composer-input-row");
    expect(feedback).toHaveStyle({
      position: "static",
      width: "100%",
      maxWidth: "100%",
    });
    expect(
      feedback.compareDocumentPosition(inputRow) &
        Node.DOCUMENT_POSITION_FOLLOWING,
    ).toBeTruthy();
    expect(within(feedback).getByRole("alert")).toHaveTextContent(
      "Voice could not connect.",
    );
  });

  it("keeps GPT-Live in the left control group and the input flexible across voice states", () => {
    const statuses = ["idle", "connecting", "listening", "ended"];
    const view = renderLayout(
      [],
      <div data-testid="gpt-live-control">{statuses[0]}</div>,
    );

    for (const status of statuses) {
      view.rerender(
        <FullPageChatLayout
          messages={[]}
          onSendMessage={jest.fn()}
          isLoading={false}
          conversations={[]}
          onLoadConversation={jest.fn()}
          onNewConversation={jest.fn()}
          onSearchConversations={jest.fn()}
          onLoadMoreConversations={jest.fn()}
          conversationsLoading={false}
          currentConversationId={42}
          welcomeTitle="Welcome"
          welcomeDescription=""
          gptLiveVoiceControl={
            <div data-testid="gpt-live-control">{status}</div>
          }
        />,
      );

      const inputRow = screen.getByTestId("chat-composer-input-row");
      const voiceControl = screen.getByTestId("gpt-live-control");
      const voiceSlot = screen.getByTestId("chat-composer-gpt-live-slot");
      const textarea = screen.getByTestId("chat-composer-textarea");
      expect(within(inputRow).getByRole("textbox")).toBeInTheDocument();
      expect(within(inputRow).getByTestId("gpt-live-control")).toBe(
        voiceControl,
      );
      expect(screen.queryByTestId("chat-composer-gpt-live-row")).toBeNull();
      expect(
        voiceSlot.compareDocumentPosition(textarea) &
          Node.DOCUMENT_POSITION_FOLLOWING,
      ).toBeTruthy();
      expect(textarea).toHaveStyle({ flex: "1" });
      expect(voiceSlot).toHaveStyle({
        flex: "0 0 112px",
        width: "112px",
        minWidth: "112px",
      });
    }
  });
});
